/**
 * Formats a phone number to a more readable format
 * @param {string} phoneNumber - The phone number to format
 * @param {string} format - The desired format ('us', 'international', 'display')
 * @returns {string} - The formatted phone number
 */
export const formatPhoneNumber = (phoneNumber, format = "display") => {
  if (!phoneNumber) return ""

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, "")

  // Handle different formats
  switch (format) {
    case "us":
      // Format: (XXX) XXX-XXXX
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      } else if (digits.length === 11 && digits[0] === "1") {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      break

    case "international":
      // Format: +X (XXX) XXX-XXXX
      if (digits.length === 10) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      } else if (digits.length === 11 && digits[0] === "1") {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      break

    case "display":
    default:
      // Smart formatting based on input
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      } else if (digits.length === 11 && digits[0] === "1") {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      } else if (digits.length > 11) {
        // For international numbers, just add spaces every 3-4 digits
        return phoneNumber.replace(/(\d{3,4})(?=\d)/g, "$1 ")
      }
      break
  }

  // If no format matches, return the original with some basic formatting
  return phoneNumber
}

/**
 * Creates a clickable phone link
 * @param {string} phoneNumber - The phone number
 * @param {string} displayText - Optional display text (defaults to formatted number)
 * @returns {string} - HTML string with tel: link
 */
export const createPhoneLink = (phoneNumber, displayText = null) => {
  if (!phoneNumber) return ""

  const formattedNumber = displayText || formatPhoneNumber(phoneNumber)
  const telNumber = phoneNumber.replace(/\D/g, "")

  return `<a href="tel:+${telNumber}" class="hover:underline">${formattedNumber}</a>`
}

/**
 * Validates if a phone number is valid
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false

  const digits = phoneNumber.replace(/\D/g, "")

  // US phone numbers: 10 digits or 11 digits starting with 1
  if (digits.length === 10) return true
  if (digits.length === 11 && digits[0] === "1") return true

  // International: 7-15 digits
  if (digits.length >= 7 && digits.length <= 15) return true

  return false
}
